import {
    addValueToParamsList,
    modifyParamsRemovePage,
    modifyParamsWithTaxonomyAll,
    modifyParamsWithTaxonomySelected,
    parseQueryParams,
} from '@/utils/queryParams';
import { CategoryBackLinks } from './components/CategoryBackLinks/CategoryBackLinks';
import { Chip } from '@liveauctioneers/hammer-ui-core/chips';
import { ConditionalMenuContainer } from '../ConditionalMenuContainer/ConditionalMenuContainer';
import { CurrentOpenMenu, SearchFacet, SearchFacetItem } from '@/pages/SearchPageSimplified/types';
import { FilterAction } from '@/types/SearchFacet';
import { getAggregationsOnDemandStatus } from '@/redux/modules/search/search.selectors';
import {
    getArchivedSearchFacets,
    getRawArchivedSearchFacets,
    getRawUpcomingSearchFacets,
    getSelectedCategoryFacetValue,
    getUpcomingSearchFacets,
} from '@/redux/modules/searchFacetAggregates.selectors';
import { getSearchUrlWithParams } from '@/utils/urls';
import { H5 } from '@liveauctioneers/hammer-ui-core/text';
import { Loading } from '@liveauctioneers/hammer-ui-core/loading';
import { MouseEvent, useCallback, useMemo } from 'react';
import { SubtleMonochromeLink } from '@liveauctioneers/hammer-ui-core/link';
import { useAppSelector } from '@/redux/hooks';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { useLocation } from 'react-router-dom';
import { useNavigateSearchPageWithParams } from '@/pages/SearchPageSimplified/hooks/useNavigateSearchPageWithParams';
import FacetTypes from '@/types/search/enums/facetTypes';
import paramTypes from '@/types/search/enums/paramTypes';

type Props = {
    currentOpenMenu?: CurrentOpenMenu;
    isAllFilters?: boolean;
    isArchivedSearch: boolean;
    onMenuClose: () => void;
    onMenuOpen: (currentMenu: CurrentOpenMenu) => void;
};

export const CategoryFilter = ({ currentOpenMenu, isAllFilters, isArchivedSearch, onMenuClose, onMenuOpen }: Props) => {
    const { formatMessage } = useIntl();
    const { search } = useLocation();
    const queryParams = parseQueryParams(search);
    const currentCategoryId = queryParams[paramTypes.CATEGORY_ID];

    const rawArchivedSearchFacets = useAppSelector(getRawArchivedSearchFacets);
    const rawUpcomingSearchFacets = useAppSelector(getRawUpcomingSearchFacets);
    const rawFacets = isArchivedSearch ? rawArchivedSearchFacets : rawUpcomingSearchFacets;

    const nestedCategoryFacet = useAppSelector((state) =>
        getSelectedCategoryFacetValue(state, isArchivedSearch, FacetTypes.CATEGORY_ID)
    );

    const navigateWithParams = useNavigateSearchPageWithParams();

    const handleNavigation = useCallback(
        (event: MouseEvent<HTMLAnchorElement | HTMLInputElement>, params: any) => {
            event.preventDefault();
            const keepMenuOpenValue = isAllFilters ? CurrentOpenMenu.AllFilters : CurrentOpenMenu.Category;

            navigateWithParams({
                checkForPageRemoval: true,
                currentOpenMenu: keepMenuOpenValue,
                filterAction: params.category_id ? FilterAction.CategoryChanged : FilterAction.CategoryRemoved,
                params,
                preventScrollReset: true,
            });
        },
        [isAllFilters, navigateWithParams]
    );

    const buildCategoryHref = (categoryId: string, remove?: boolean) => {
        let newParams = modifyParamsRemovePage(queryParams);
        if (remove) {
            delete newParams[paramTypes.CATEGORY_ID];
        } else if (currentCategoryId?.includes(',')) {
            newParams = modifyParamsWithTaxonomySelected(newParams, FacetTypes.CATEGORY_ID, categoryId, rawFacets);
        } else {
            delete newParams[paramTypes.CATEGORY_ID];
            newParams = addValueToParamsList(newParams, paramTypes.CATEGORY_ID, categoryId);
        }

        return newParams;
    };

    const onClearCategoryFromSearch = useCallback(
        (event: MouseEvent<HTMLInputElement>) => {
            let newParams = modifyParamsRemovePage(queryParams);

            delete newParams[paramTypes.CATEGORY_ID];
            newParams = modifyParamsWithTaxonomyAll(newParams, FacetTypes.CATEGORY_ID, currentCategoryId);
            handleNavigation(event, newParams);
        },
        [currentCategoryId, handleNavigation, queryParams]
    );

    const archivedSearchFacets = useAppSelector(getArchivedSearchFacets);
    const upcomingFacets = useAppSelector(getUpcomingSearchFacets);
    const aggregationsOnDemandStatus = useAppSelector(getAggregationsOnDemandStatus);
    const facets = isArchivedSearch ? archivedSearchFacets : upcomingFacets;

    const categoryFacet = useMemo(() => {
        return facets.find((facet) => facet.id === FacetTypes.CATEGORY_ID) as SearchFacet | undefined;
    }, [facets]);

    const categoryLabel = formatMessage({ id: `search_facet_title_${FacetTypes.CATEGORY_ID}` });

    if (!categoryFacet) {
        if (isArchivedSearch && aggregationsOnDemandStatus !== 'error') {
            // Archived search without facets yet: show a placeholder chip so users can click
            // to trigger on-demand facet loading via onMenuOpen → handleMenuOpen
            return (
                <ConditionalMenuContainer
                    currentOpenMenu={currentOpenMenu}
                    currentOpenMenuId={CurrentOpenMenu.Category}
                    isAllFilters={isAllFilters}
                    onMenuClose={onMenuClose}
                    onMenuOpen={onMenuOpen}
                    title={categoryLabel}
                    trigger={<Chip style="singleFilter">{categoryLabel}</Chip>}
                >
                    <Loading />
                </ConditionalMenuContainer>
            );
        }
        return null;
    }
    const { categories, id } = categoryFacet;

    // Look for current category in both top-level and nested categories
    const currentCategory: SearchFacetItem | undefined = currentCategoryId
        ? categoryFacet?.categories?.facets?.find((category) => category.id === currentCategoryId) ||
          nestedCategoryFacet?.categories?.find?.((category) => category.id === currentCategoryId) ||
          // If nestedCategoryFacet itself matches the ID, use it directly
          (nestedCategoryFacet?.id === currentCategoryId ? nestedCategoryFacet : undefined)
        : undefined;

    return (
        <ConditionalMenuContainer
            accordionDefaultOpen
            currentOpenMenu={currentOpenMenu}
            currentOpenMenuId={CurrentOpenMenu.Category}
            isAllFilters={isAllFilters}
            onMenuClose={onMenuClose}
            onMenuOpen={onMenuOpen}
            title={formatMessage({ id: `search_facet_title_${id}` })}
            trigger={
                <Chip
                    onClose={currentCategoryId ? onClearCategoryFromSearch : undefined}
                    selected={Boolean(currentCategoryId)}
                    style="singleFilter"
                >
                    {currentCategory?.name || 'Category'}
                </Chip>
            }
        >
            <CategoryBackLinks
                currentOpenMenu={currentOpenMenu}
                isAllFilters={isAllFilters}
                isArchivedSearch={isArchivedSearch}
                rawFacets={rawFacets}
            />

            {/* L1 Categories */}
            {Boolean(!nestedCategoryFacet.categories) &&
                categories?.facets[0]?.id !== currentCategoryId &&
                categories.facets.map((option) => (
                    <SubtleMonochromeLink
                        href={getSearchUrlWithParams(buildCategoryHref(option.id))}
                        key={option.id}
                        onClick={(event) => handleNavigation(event, buildCategoryHref(option.id))}
                    >
                        {option.name}
                    </SubtleMonochromeLink>
                ))}
            {/* ALL OTHER LEVELS */}
            {Boolean(nestedCategoryFacet.categories) && (
                <>
                    <H5 className="text-link-primary">{nestedCategoryFacet.name}</H5>
                    {nestedCategoryFacet.categories.map((option) => (
                        <SubtleMonochromeLink
                            href={getSearchUrlWithParams(buildCategoryHref(option.id))}
                            key={option.id}
                            onClick={(event) => handleNavigation(event, buildCategoryHref(option.id))}
                        >
                            {option.name}
                        </SubtleMonochromeLink>
                    ))}
                </>
            )}
        </ConditionalMenuContainer>
    );
};
